import Checkbox from '@/app/components/common/Checkbox'
import Text from '@/app/components/common/Text'
import { FACILITIES } from '@/app/constants'
import { Filters } from '@/app/types'
import { AmenityName, getAmenityIcon } from '@/app/utils/amenityIcons'
import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { FC, useMemo } from 'react'

interface AmenitiesFilterProps {
	localValue: Filters
	showAllFacilities: boolean
	availableFilters: {
   		id: number;
    	name: string;
	}[],
	toggleFacilitiesView: () => void
	handleFacilityChange: (facilityIds: number | number[], name: string) => void
}

const AmenitiesFilter: FC<AmenitiesFilterProps> = ({
	localValue,
	showAllFacilities,
	availableFilters,
	handleFacilityChange,
	toggleFacilitiesView,
}) => {
	// Check if any ID in a group is selected
	const isGroupSelected = (id: number): boolean => {
		return localValue.facilities.includes(id)
	}

	const shouldExpandList = useMemo(() => {
		return availableFilters.some((option, index) => {
			const isSelected = localValue.facilities.includes(option.id)
			return isSelected && index >= 5
		})
	}, [localValue.facilities, availableFilters])

	const t = useTranslations('Keywords')
	const tFilters = useTranslations('Filters.Amenities')

	const facilitiesToShow =
		shouldExpandList || showAllFacilities
			? availableFilters
			: availableFilters.slice(0, 5)

	if (availableFilters.length === 0) return null

	return (
		<div className="hotel-list-filters__section border-none mb-0">
			<Text size="base" font={'bold'} className="hotel-list-filters__title">
				{t('amenitiesFilter')}
			</Text>
			<ul className="hotel-list-filters__facilities">
				{facilitiesToShow.map(({ id, name }) => {
					const isSelected = isGroupSelected(id)
					// Use first ID as key for the group
					const groupKey = id
					const labelText = `${tFilters(name)}`
					const icon = getAmenityIcon(name as AmenityName)

					return (
						<div
							key={groupKey}
							className="hotel-list-filters__facilities-item hotel-list-filters__item"
						>
							{/* Desktop: Regular checkbox */}
							<div className="xl:block hidden w-full">
							<Checkbox
								variant="main-dark"
								checked={isSelected}
								testId={`facility-${groupKey}`}
								onChange={() => handleFacilityChange(id, name)}
								labelRight={labelText}
								bold={false}
							/>
						</div>
						{/* Mobile: Pill button with icon */}
						<button
							type="button"
							className={classNames(
								'hotel-list-filters__pill-checkbox xl:hidden',
								{
									'pill-checkbox--active': isSelected,
								}
							)}
							onClick={() => handleFacilityChange(id, name)}
							data-test-id={`facility-${groupKey}`}
						>
							{icon && (
								<span className="pill-checkbox__icon">{icon}</span>
							)}
							<span className="pill-checkbox__text">{labelText}</span>
							</button>
						</div>
					)
				})}
			</ul>
			{!shouldExpandList && availableFilters.length > 5 && (
				<button
					onClick={toggleFacilitiesView}
					className="text-secondary font-medium text-sm hover:underline mt-2 mb-2 max-sm:underline"
				>
					{showAllFacilities ? t('seeLess') : t('seeMore')}
				</button>
			)}
		</div>
	)
}

export default AmenitiesFilter
